import React, { useEffect, useState, useMemo } from 'react';
import { Button, Chip, Divider, Grid, styled, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SiteDTO, TransformUserRoleRequest, UserRole, TransformUserRoleDTO } from '@beable/roster-backend-client-v1';
import { Autocomplete } from '@mui/material';
import { uniqueId, } from 'lodash';
import transformService from '../../services/TransformService';
import SuccessDialog from './SuccessDialog';
import Search from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Loading } from '../Common/Layout/Loading';
import { getStyles } from './styles';

const Form = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(1),
}));

type Option = {
    value: string;
    label: string;
};

interface TransformRoleData {
    user: Option;
    role: UserRole | '';
}

const defaultState: TransformRoleData = {
    user: { value: '', label: '' },
    role: ''
};

const roleOptions: Option[] = [
    { value: UserRole.teacher, label: 'Teacher' },
    { value: UserRole.student, label: 'Student' },
    { value: UserRole.administrator, label: 'Administrator' }
];

interface RoleTransformProps {
    site: SiteDTO | undefined;
    isSiteSelected: boolean;
}

export default function RoleTransform({ site, isSiteSelected }: RoleTransformProps) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [transformRole, setTransformRole] = useState<TransformRoleData>(defaultState);
    const { user, role } = transformRole;

    const [userOptions, setUserOptions] = useState<Option[]>([]);
    const [existingRecords, setExistingRecords] = useState<TransformUserRoleDTO[]>([]);
    const [loadingRecords, setLoadingRecords] = useState(false);

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [forceReset, setForceReset] = useState('');
    const [dialog, showDialog] = useState(false);
    const [successSaving, setSuccessSaving] = useState(false);
    const [expandedRecord, setExpandedRecord] = useState<string | null>(null);
    const [recordSearchTerm, setRecordSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('');

    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [midSearch, setMidSearch] = useState(false);
    const [searchedTerm, setSearchedTerm] = useState('');

    useEffect(() => {
        handleResetForm();
        const getData = async (siteId: number) => {
            await fetchExistingRecords(siteId);
        }
        if (site?.id) { getData(site.id); }
        else {
            setExistingRecords([]);
        }
    }, [site]);

    const handleSelectChange =
        (prop: keyof TransformRoleData, value: any) => {
            setTransformRole({
                ...transformRole,
                [prop]: value,
            });
        };

    const handleInputChange = (event: any) => {
        event.persist();
        setSearchTerm(event.target.value);
        if (hasSearched) {
            setMidSearch(true);
        }
        setHasSearched(false);
    };
    const handleKeyPress = (event: any) => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleUserSearch();
        }
    };
    const handleUserSearch = async () => {
        setIsSearchLoading(true);

        const result = await transformService.getUsersBySubstring(
            site!.id,
            searchTerm
        );
        const userOptions = !result ? [] : result.map((user) => ({
            value: user.externalSourceId ?? '',
            label: user.username
        }));
        setUserOptions(userOptions);
        setSearchedTerm(searchTerm);
        setHasSearched(true);
        setIsSearchLoading(false);
        setMidSearch(false);
    };

    const fetchExistingRecords = async (siteId: number) => {
        setLoadingRecords(true);
        const data = await transformService.getTransformUserRoles(siteId);
        console.log('data', data);
        if (data) {
            setExistingRecords(data);
        } else {
            setExistingRecords([]);
        }
        setLoadingRecords(false);
    };

    const handleResetForm = () => {
        setTransformRole(defaultState);
        setForceReset(uniqueId());
        setUserOptions([]);
        setSearchTerm('');
        setIsSearchLoading(false);
        setHasSearched(false);
        setSearchedTerm('');
    };

    const isFormValid = () => {
        if (!transformRole.user || transformRole.user?.value === '') {
            return false;
        }
        if (transformRole.role === '' || !transformRole.role) {
            return false;
        }
        return true;
    };

    const handleSaveForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormSubmitting(true);

        let userRoleTransform: TransformUserRoleRequest;
        if (role !== '') {
            userRoleTransform = {
                siteId: site?.id ?? 0,
                role,
                userSourcedId: user.value
            };
        }
        else {
            return;
        }

        const response = await transformService.saveAddUserRoleTransformation(userRoleTransform);

        setFormSubmitting(false);
        setSuccessSaving(true)

        if (!response) {
            setSuccessSaving(false);
        }
        showDialog(true);
    };

    const closeDialog = () => {
        setSuccessSaving(false);
        showDialog(false);
        handleResetForm();
        if (site?.id) {
            fetchExistingRecords(site.id);
        }
    };

    const handleViewRecord = (record: TransformUserRoleDTO) => {
        if (expandedRecord === record.id) {
            // If already expanded, hide it
            setExpandedRecord(null);
        } else {
            // If not expanded, show it
            setExpandedRecord(record.id || null);
        }
    };

    const filteredRecords = useMemo(() => {
        return existingRecords.filter(record => {
            const matchesSearch = !recordSearchTerm || 
                record.sourcedId?.toLowerCase().includes(recordSearchTerm.toLowerCase());
            
            const matchesRole = !filterRole || record.role === filterRole;
            
            return matchesSearch && matchesRole;
        });
    }, [existingRecords, recordSearchTerm, filterRole]);

    return (
        <>
            {dialog === true && <SuccessDialog closeDialog={closeDialog} success={successSaving} />}
            <Grid size={12}>
                <Grid container>
                    <Grid
                        sx={{ display: "flex", justifyContent: "flex-start", marginTop: '6px' }}
                        size={12}>
                        <Typography variant='h5' align-left='true'>
                            Tranform selected user's role.
                        </Typography>
                    </Grid>
                    <Grid sx={{ display: "flex", justifyContent: "flex-start" }} size={12}>

                        <p align-left='true'>
                            Select the user that you would like to update, and the role you would like to update to. <br />
                            This will add records to tranformUserRole. <br />
                            To apply this transformation to the site, roster a batch. Once completed, the user's role will be updated.
                        </p>
                    </Grid>
                </Grid>
                <Form onSubmit={handleSaveForm}>
                    <Grid sx={styles.formContainer}>
                        <Grid sx={styles.formField}>
                            <TextField
                                variant='outlined'
                                label='Search for user'
                                name='search'
                                data-testid='search'
                                color='primary'
                                helperText='Search by first name, last name or username'
                                value={searchTerm}
                                disabled={!isSiteSelected || isSearchLoading}
                                slotProps={{
                                    input: {
                                        endAdornment: <Button
                                            endIcon={isSearchLoading ? <Loading size={20} height={'20px'} /> : <Search />}
                                            variant="contained"
                                            aria-label="search-button"
                                            color="primary"
                                            disabled={isSearchLoading || !site || !searchTerm}
                                            onClick={() => handleUserSearch()}
                                        >
                                            Search
                                        </Button>
                                    }
                                }}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Divider>
                                {!userOptions.length &&
                                    ((midSearch && !hasSearched) || hasSearched) ?
                                    <Chip
                                        sx={styles.chip}
                                        label={<>No users found with name {<><Typography component="span" sx={{ fontWeight: "bold" }}>{searchedTerm}</Typography></>}</>}
                                    /> : undefined}
                            </Divider>
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Autocomplete
                                key={forceReset}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'user'}
                                aria-label='user-dropdown'
                                options={userOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected || !userOptions.length}
                                onChange={(_event, value) => handleSelectChange('user', value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label={!userOptions.length ? 'Search for valid user' : 'User'}
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: user?.value === ''
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Autocomplete
                                key={forceReset}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'role'}
                                aria-label='role-dropdown'
                                options={roleOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected || !userOptions.length}
                                onChange={(_event, value) => handleSelectChange('role', value?.value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label={!userOptions.length ? 'Search for valid user' : 'Role'}
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: role === ''
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formButtons} >
                            <Button onClick={handleResetForm} sx={styles.saveForm}>
                                Cancel
                            </Button>
                            <Button
                                color='primary'
                                sx={styles.saveForm}
                                type='submit'
                                disabled={!isFormValid() || formSubmitting}
                                variant='contained'
                                aria-label='save-button'
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </Form >
            </Grid >
            {/* Existing Records Section */}
            <Grid sx={{ marginTop: '32px' }} size={12}>
                <Typography variant="h6" sx={{ marginBottom: '16px' }}>
                    Existing Transform User Role Records ({filteredRecords.length})
                </Typography>
                
                {/* Search and Filter Controls */}
                <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Search records"
                            placeholder="Search by sourced ID..."
                            value={recordSearchTerm}
                            onChange={(e) => setRecordSearchTerm(e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <Autocomplete
                            fullWidth
                            options={roleOptions}
                            value={roleOptions.find(option => option.value === filterRole) || null}
                            onChange={(_event, value) => setFilterRole(value?.value || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Filter by role"
                                    size="small"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                {loadingRecords ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            p: 3
                        }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sourced ID</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRecords.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            {existingRecords.length === 0 
                                                ? 'No existing transform user role records found'
                                                : 'No records match the current search criteria'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredRecords.map((record, index) => (
                                        <React.Fragment key={record.id || index}>
                                            <TableRow>
                                                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.sourcedId}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={record.role} 
                                                        color={record.role === 'student' ? 'primary' : record.role === 'teacher' ? 'secondary' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={expandedRecord === record.id ? "Hide Details" : "View Details"}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleViewRecord(record)}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            {expandedRecord === record.id && (
                                                <TableRow>
                                                    <TableCell colSpan={3}>
                                                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                            <Grid container spacing={2}>
                                                                <Grid
                                                                    size={{
                                                                        xs: 12,
                                                                        sm: 6
                                                                    }}>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                        <strong>ID:</strong> {record.id}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                        <strong>Site ID:</strong> {record.siteId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                        <strong>Sourced ID:</strong> {record.sourcedId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                        <strong>Role:</strong> {record.role}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Grid>
        </>
    );
}
